import { useCallback, useEffect, useMemo } from 'react'

import { useAtom, useSetAtom } from 'jotai'
import { useUpdateNodeInternals } from '@xyflow/react'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'

import { useNode } from 'Automations/nodeDefinitions/hooks'
import {
  autoOpenConditionIdAtom,
  selectedGroupIdAtom
} from '../../switch.atoms'
import { createBlankGroup } from '../../helpers'
import { Config, SwitchGroup } from '../..'

type UseGroups = (params: { nodeId: string }) => {
  groups: SwitchGroup[]
  addGroup: () => void
  removeGroup: () => void
  reorderGroups: (event: DragEndEvent) => void
  canRemoveGroup: boolean
  canReorderGroups: boolean
}

export const useGroups: UseGroups = ({ nodeId }) => {
  const { nodeData, setNodeData } = useNode<Config>({ nodeId })
  const [selectedGroupId, setSelectedGroupId] = useAtom(selectedGroupIdAtom)
  const setAutoOpenConditionId = useSetAtom(autoOpenConditionIdAtom)
  const updateNodeInternals = useUpdateNodeInternals()

  const groups = useMemo(() => {
    return nodeData?.groups ?? []
  }, [nodeData?.groups])

  const groupOrderKey = useMemo(
    () => groups.map(({ id }) => id).join(','),
    [groups]
  )

  /**
   * When the order of groups changes via drag and drop, react-flow needs a nudge
   * to recalculate the positions of connected edges
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment
  useEffect(() => {
    updateNodeInternals(nodeId)
  }, [updateNodeInternals, nodeId, groupOrderKey])

  const addGroup = useCallback(() => {
    const blankGroup = createBlankGroup()
    setNodeData('groups', [...groups, blankGroup])
    setSelectedGroupId(blankGroup.id)
    setAutoOpenConditionId(blankGroup.conditions[0].id)
  }, [groups, setNodeData, setSelectedGroupId, setAutoOpenConditionId])

  const removeGroup = useCallback(() => {
    if (groups.length <= 1) return

    const removingGroupIndex = groups.findIndex(
      ({ id }) => id === selectedGroupId
    )

    const updatedGroups = groups.filter(({ id }) => id !== selectedGroupId)

    setSelectedGroupId(
      updatedGroups.at(Math.max(0, removingGroupIndex - 1))?.id
    )

    setNodeData('groups', updatedGroups)
  }, [groups, selectedGroupId, setNodeData, setSelectedGroupId])

  const reorderGroups = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const oldIndex = groups.findIndex(({ id }) => id === active.id)
      const newIndex = groups.findIndex(({ id }) => id === over.id)

      setNodeData('groups', arrayMove(groups, oldIndex, newIndex))
    },
    [groups, setNodeData]
  )

  const canRemoveGroup = useMemo(() => {
    return groups.length > 1
  }, [groups])

  const canReorderGroups = useMemo(() => {
    return groups.length > 1
  }, [groups])

  return {
    groups,
    addGroup,
    removeGroup,
    reorderGroups,
    canRemoveGroup,
    canReorderGroups
  }
}
